import { useState, useEffect } from 'react';
import { getIncomingLeads, acceptLead, rejectLead } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Sparkles, MapPin, CheckCircle2, XCircle, Clock, Award, FileText } from 'lucide-react';

export default function CompanyLeadsSection() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await getIncomingLeads();
      setLeads(r.data);
    } catch (e) {
      toast.error('Kunne ikke hente indkommende leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAccept = async (leadId) => {
    setBusy(leadId);
    try {
      await acceptLead(leadId);
      toast.success('Du har vist interesse. Kunden vælger nu hvem de vil arbejde med.');
      setLeads(leads.filter((l) => l.id !== leadId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke acceptere');
    } finally {
      setBusy('');
    }
  };

  const handleReject = async (leadId) => {
    if (!window.confirm('Er du sikker på, at du vil afvise denne opgave?')) return;
    setBusy(leadId);
    try {
      await rejectLead(leadId);
      toast.success('Lead afvist');
      setLeads(leads.filter((l) => l.id !== leadId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke afvise');
    } finally {
      setBusy('');
    }
  };

  if (loading) {
    return <Card><CardContent className="p-6 space-y-3">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}</CardContent></Card>;
  }

  return (
    <Card data-testid="company-leads-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#c97f59]" />
          Automatiske leads
        </CardTitle>
        <CardDescription>
          Anmodninger fra kunder der matcher din kategori & område. Vis interesse eller afvis – kunden vælger derefter selv.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {leads.length === 0 ? (
          <div className="text-center py-10">
            <Sparkles className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground text-sm">Ingen indkommende leads lige nu</p>
            <p className="text-xs text-muted-foreground mt-1">Vi sender dig en notifikation når en kunde matcher dit område</p>
          </div>
        ) : (
          <div className="space-y-4">
            {leads.map((lead) => {
              const mine = (lead.assignments || []).find((a) => a.status === 'pending');
              return (
                <div key={lead.id} className="rounded-xl border p-4 space-y-3" data-testid={`incoming-lead-${lead.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="font-semibold">{lead.category}</h4>
                        <Badge className="bg-[#f5e6d8] text-[#6b3e25]">
                          <Clock className="w-3 h-3 mr-1" />Ny
                        </Badge>
                        {mine && (
                          <Badge className="bg-stone-100 text-stone-700">
                            <Award className="w-3 h-3 mr-1" />
                            {mine.distance_km} km væk
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />Postnr. {lead.postal_code}</span>
                        <span>{new Date(lead.created_at).toLocaleDateString('da-DK')}</span>
                        {lead.preferred_date && <span>Ønsket start: {lead.preferred_date}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-stone-50 border text-sm">
                    <p className="font-medium mb-1 flex items-center gap-1"><FileText className="w-3 h-3" />Beskrivelse</p>
                    <p className="text-muted-foreground">{lead.description}</p>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button
                      size="sm"
                      className="bronze-shine border-0 text-white"
                      disabled={busy === lead.id}
                      onClick={() => handleAccept(lead.id)}
                      data-testid={`accept-lead-${lead.id}`}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1.5" />
                      {busy === lead.id ? 'Sender...' : 'Vis interesse'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      disabled={busy === lead.id}
                      onClick={() => handleReject(lead.id)}
                      data-testid={`reject-lead-${lead.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-1.5" />Afvis
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground italic">
                    Når du viser interesse, får kunden besked og kan vælge dig til at starte chatten.
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
